import type { GetApiBillingPredictedIncomeResponse } from "@/lib/api_client/types.gen";

export type Forecast = GetApiBillingPredictedIncomeResponse;
export type BreakdownRow = Forecast["breakdowns"][number];
export type Measure = "income" | "invoices" | "customers" | "average";
export type Dimension = BreakdownRow["dimension"];
export const MEASURES: Record<Measure, { label: string; definition: string }> =
  {
    income: {
      label: "Forventet inntekt",
      definition:
        "Summen av linjebeløp × antall på PENDING-fakturaer i Periode, vist i kroner. Dette er planlagte fakturaer, ikke betalt inntekt eller en prognose for nye salg.",
    },
    invoices: {
      label: "Fakturaer",
      definition:
        "Antall PENDING-fakturaer returnert av Periode for måneden, også fakturaer med nullbeløp. Én kunde kan ha flere fakturaer.",
    },
    customers: {
      label: "Unike kunder",
      definition:
        "Kunder med minst én forventet faktura i måneden og en kobling til en lokal kunde. Hver kunde telles én gang per måned og segment. En kunde kan finnes i flere abonnementstyper; summen av segmentene kan derfor overstige antall unike kunder totalt. Ukoblede fakturaer gir ikke et estimert kundeantall.",
    },
    average: {
      label: "Inntekt per kunde",
      definition:
        "Inntekt fra fakturaer koblet til kunder ÷ unike kunder i samme måned og segment. Ukoblede fakturaer holdes utenfor både teller og nevner. Segmentenes gjennomsnitt kan ikke summeres, og vises derfor som separate søyler, ikke stablet.",
    },
  };
export const DIMENSIONS: Record<
  Dimension,
  { label: string; definition: string }
> = {
  product: {
    label: "Abonnementstype",
    definition:
      "Abonnementets tier fra siste lokale synkronisering, samme datapunkt som abonnementstype i Kunder, for eksempel healthcheck_annually_2025_v3. Vi bruker typen på abonnementet fakturaen tilhører, koblet via Periode-abonnements-ID, ikke produktnavnet eller kundens andre abonnementer. Manglende type eller kobling vises som Ukjent. Hentingen fra Periode mellomlagres på serveren i opptil fem minutter.",
  },
  source: {
    label: "Kundekilde",
    definition:
      "Kundens sist registrerte hovedkategori, for eksempel Podcast, Google eller Instagram. Fritekstsvar som navnet på en podcast eller et SATS-senter vises ikke. Ukjente kategorier samles under Annet. Manglende kilde vises som Ukjent. Hver kunde har én kilde i denne fordelingen.",
  },
  gender: {
    label: "Kjønn",
    definition:
      "Registrert kjønn i kundens nyeste profil. Manglende verdi vises som Ukjent. Tallene beskriver eksisterende kunder, ikke årsakssammenhenger.",
  },
  age: {
    label: "Aldersgruppe",
    definition:
      "Alder i dag, beregnet fra fødselsdato i kundens nyeste profil. Intervaller: under 30, 30–39, 40–49, 50–59, 60–69 og 70+. Manglende eller ugyldig fødselsdato vises som Ukjent.",
  },
  tenure: {
    label: "Kundeansiennitet",
    definition:
      "Hvor lenge det er siden kunden startet sitt første kjente betalte abonnement, målt frem til i dag. Eksempel: Første abonnement startet for 18 måneder siden → kunden ligger i «1–2 år» i alle prognosemånedene. Hvis kunden har hatt en pause eller byttet abonnement, teller tiden fortsatt fra den første starten. Det betyr altså ikke 18 måneder med sammenhengende medlemskap. Vi bruker den tidligste startdatoen vi kjenner fra abonnements- og betalingshistorikken; hvis ingen dato er kjent, vises «Ukjent».",
  },
};
export const VIEW_DEFINITION =
  "Beløp viser valgt måltall. Prosent viser hvert segments andel av summen av segmentene i samme måned, ikke av hele perioden. Kundeandeler per abonnementstype er andeler av kunde–abonnementstype-koblinger, siden én kunde kan ha flere abonnementstyper. Inntekt per kunde kan ikke vises som prosent.";
export const LABEL_DEFINITION =
  "Hver søyle er én måned, og hver farge er ett abonnement eller én kundegruppe. Tallene i fargene legges sammen til månedstotalen over søylen. Eksempel: 3 000 kr + 7 000 kr = 10 000 kr. I prosentvisning blir tallene i fargene 30 % og 70 %, men månedstotalen er fortsatt 10 000 kr. Pek på søylen for å se både tall og prosent.";
export const PERIOD_DEFINITION =
  "Forventet inntekt og antall fakturaer for hele den valgte perioden, som i den første grafen. Totalen er den samme uansett fordeling eller visning. Hvis en måned ikke kan hentes, er bare tilgjengelige måneder med i totalen.";

export function parseMeasure(value: string): Measure {
  return Object.hasOwn(MEASURES, value) ? (value as Measure) : "income";
}
export function parseDimension(value: string): Exclude<Dimension, "product"> {
  return value !== "product" && Object.hasOwn(DIMENSIONS, value)
    ? (value as Exclude<Dimension, "product">)
    : "source";
}
export function measureValue(
  row: Pick<
    BreakdownRow,
    "totalAmount" | "invoiceCount" | "customerCount" | "matchedAmount"
  >,
  measure: Measure,
): number | null {
  if (measure === "income") return row.totalAmount / 100;
  if (measure === "invoices") return row.invoiceCount;
  if (measure === "customers") return row.customerCount;
  return row.customerCount ? row.matchedAmount / 100 / row.customerCount : null;
}

export function breakdownChartData(
  forecast: Forecast,
  dimension: Dimension,
  measure: Measure,
  percent: boolean,
) {
  const rows = forecast.breakdowns.filter((row) => row.dimension === dimension);
  const orderedLabels =
    dimension === "age"
      ? ["Under 30", "30–39", "40–49", "50–59", "60–69", "70+"]
      : dimension === "tenure"
        ? ["Under 6 måneder", "6–11 måneder", "1–2 år", "2–3 år", "3+ år"]
        : [];
  const segments = [
    ...new Map(rows.map((row) => [row.segment, row.label])).entries(),
  ]
    .sort((a, b) =>
      a[1] === "Ukjent"
        ? 1
        : b[1] === "Ukjent"
          ? -1
          : orderedLabels.length
            ? orderedLabels.indexOf(a[1]) - orderedLabels.indexOf(b[1])
            : a[1].localeCompare(b[1], "nb"),
    )
    .map(([segment, label], index) => ({
      segment,
      label,
      key: `segment${index}`,
    }));
  const data = forecast.months.map((month) => {
    const monthly = new Map(
      rows
        .filter((row) => row.month === month.month)
        .map((row) => [row.segment, row]),
    );
    const sum = [...monthly.values()].reduce(
      (total, row) => total + (measureValue(row, measure) ?? 0),
      0,
    );
    const values: Record<string, string | number | null> = {
      month: month.month,
      stackTotal: month.available && measure !== "average" ? sum : null,
      stackHeight:
        month.available && measure !== "average"
          ? percent && sum !== 0
            ? 100
            : sum
          : null,
    };
    for (const segment of segments) {
      const row = monthly.get(segment.segment);
      const value = !month.available
        ? null
        : row
          ? measureValue(row, measure)
          : measure === "average"
            ? null
            : 0;
      values[`${segment.key}Absolute`] = value;
      values[`${segment.key}Percent`] =
        measure !== "average" && value !== null
          ? sum
            ? (value / sum) * 100
            : 0
          : null;
      values[segment.key] =
        percent && measure !== "average" && value !== null
          ? sum
            ? (value / sum) * 100
            : 0
          : value;
    }
    return values;
  });
  return { rows, segments, data };
}
